<template>
  <div class="header-search-wrapper" v-click-outside="clickOutside">
    <div class="header-search--overlay" v-if="isPopupShown" @click="SET_INPUT_FOCUSED_ACTION(false)" :style="{'height': overlayHeight}"></div>
    <div class="header-search" :class="{ 'header-search--mobile-focus': isInputFocused }">
      <button class="header-search__arrow" @click="hideOverlay">
        <svg width="16"
             height="16"
             viewBox="0 0 16 16"
             fill="none"
             xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd"
                clip-rule="evenodd"
                d="M3.8 7L9.4 1.4L8 0L0 8L8 16L9.4 14.6L3.8 9H16V7H3.8Z"
                fill="#19191E"/>
        </svg>
      </button>
      <form class="header-search__input-box" @submit="handleRedirect">
        <label class="visually-hidden">{{ $t('search') }}</label>
        <input ref="searchInput"
               type="search"
               class="header-search__input"
               name="text"
               required
               autocomplete="off"
               title=""
               :placeholder="isInputFocused ? '' : $t('search')"
               :value="searchBarValue"
               @keydown="handleKeydown"
               @mouseenter="FETCH_SEARCH_HISTORY"
               @focus="handleFocus(true)"
               @blur="handleBlur"
               @input="handleInput">
        <span class="header-search__icon">
          <svg width="16" height="17">
            <use :xlink:href="spritePath + '#search'"></use>
          </svg>
        </span>
        <button @click.prevent="handleRedirect"
                type="submit"
                v-if="isInputFocused"
                class="header-search__focus-btn">
          <svg width="16" height="17">
            <use :xlink:href="spritePath + '#search'"></use>
          </svg>
        </button>
        <button
          type="button"
          class="modal__close"
          :class="{'modal__close--hidden': !searchBarValue, 'modal__close--desktop-hidden': isInputFocused && !searchBarValue || !isInputFocused}"
          @click="clearInput"></button>
      </form>
      <SearchPopup @allResultsAction="debouncedSearch.cancel()" @touchmove="$refs.searchInput.blur()"  :is-mobile="!dispatchAction" v-if="isPopupShown"/>
      <!--      <script v-html="json_ld_search" type="application/ld+json"></script>-->
    </div>
  </div>
</template>

<script>
import SearchPopup from './SearchPopup'
import { mapActions, mapGetters, mapState } from 'vuex'
import vClickOutside from 'v-click-outside'
import { spritePath } from '@/mixins/spritePath'
import { debounce as debouncer } from '../../helpers/debounce'

export default {
  name: 'SearchBar',
  components: { SearchPopup },
  directives: {
    clickOutside: vClickOutside.directive
  },
  mixins: [spritePath],
  props: {
    dispatchAction: Boolean
  },
  data () {
    return {
      keyboardSelected: '',
      currentItemIndexForArrows: 1,
      body: document.querySelector(`body`),
      windowWidth: window.innerWidth,
      debouncedSearch: null
    }
  },
  computed: {
    ...mapGetters(['allPopupItems']),
    ...mapState({
      searchBarValue: state => state.search.searchBarValue,
      searchBarInfo: state => state.search.searchBarInfo,
      arrowSelectedItem: state => state.search.arrowSelectedItem,
      searchHistory: state => state.search.searchHistory,
      isInputFocused: state => state.search.isInputFocused,
      shouldHistoryShow: state => state.search.shouldHistoryShow
    }),
    isPopupShown () {
      return (this.searchBarValue || this.searchHistory) && this.isInputFocused
    },
    isMobile () {
      return this.windowWidth <= 1024
    },
    overlayHeight () {
      return !this.isMobile ? '2000px' : '100vh'
    }
  },
  methods: {
    ...mapActions(['FETCH_SEARCH_BAR_INFO', 'SET_FROM_KEYBOARD', 'REMOVE_ARROW_SELECTED_ITEM', 'FETCH_SEARCH_HISTORY', 'SET_SEARCH_BAR_VALUE_ACTION', 'SET_INPUT_FOCUSED_ACTION']),
    handleOverlay (bool) {
      this.SET_INPUT_FOCUSED_ACTION(bool)
    },
    hideOverlay () {
      setTimeout(() => this.handleOverlay(false), 200)
    },
    handleFocus () {
      this.handleOverlay(true)
      if (this.searchHistory !== false) {
        this.FETCH_SEARCH_HISTORY()
      }
      if (!this.searchBarInfo) {
        this.FETCH_SEARCH_BAR_INFO(this.searchBarValue)
      }
    },
    handleRedirect (e) {
      e.preventDefault()
      this.debouncedSearch.cancel()
      if (this.searchBarInfo && this.searchBarInfo.redirect_url) {
        window.location.href = this.searchBarInfo.redirect_url
      } else if (!this.arrowSelectedItem.text && this.searchBarValue) {
        window.location.href = '/catalog/search/?text=' + this.searchBarValue
      }
    },
    handleInput ({ target }) {
      this.currentItemIndexForArrows = 1
      const trimmedValue = target.value.trim()

      this.REMOVE_ARROW_SELECTED_ITEM()
      this.SET_SEARCH_BAR_VALUE_ACTION(trimmedValue)
      this.debouncedSearch(trimmedValue)
    },
    handleKeydown (e) {
      if (e.key === 'Escape') {
        this.$refs.searchInput.blur()
        this.SET_INPUT_FOCUSED_ACTION(false)
      } else if (e.key === 'ArrowDown' && this.allPopupItems.length) {
        this.currentItemIndexForArrows++

        if (this.currentItemIndexForArrows === this.allPopupItems.length + 1) {
          this.currentItemIndexForArrows = 1
        }
        this.SET_FROM_KEYBOARD(this.currentItemIndexForArrows)
      } else if (e.key === 'ArrowUp' && this.allPopupItems.length) {
        this.currentItemIndexForArrows--

        if (this.currentItemIndexForArrows <= 0) {
          this.currentItemIndexForArrows = this.allPopupItems.length
        }
        this.SET_FROM_KEYBOARD(this.currentItemIndexForArrows)
      } else if (e.key === 'Enter') {
        this.debouncedSearch.cancel()
        if (this.searchBarInfo && this.searchBarInfo.redirect_url) {
          window.location.href = this.searchBarInfo.redirect_url
        } else if (this.arrowSelectedItem.text) {
          window.location.href = this.arrowSelectedItem.url
        }
      }
    },
    handleBlur () {
      this.currentItemIndexForArrows = 1
      this.REMOVE_ARROW_SELECTED_ITEM()
    },
    clearInput () {
      this.FETCH_SEARCH_BAR_INFO('')
      this.SET_SEARCH_BAR_VALUE_ACTION('')
      this.$refs.searchInput.focus()
    },
    handleBodyScroll () {
      (this.shouldHistoryShow || this.isPopupShown) && this.isInputFocused ? this.body.classList.add(`v--modal-block-scroll`) : this.body.classList.remove(`v--modal-block-scroll`)
    },
    handleResize () {
      this.windowWidth = window.innerWidth
    },
    clickOutside (e) {
      if (!this.isInputFocused) return
      let isEnd = true
      let currentNode = e.target
      while (isEnd && currentNode) {
        if (typeof currentNode.className === 'string' && currentNode.className.includes('header-search-wrapper')) {
          isEnd = false
        } else if (typeof currentNode.className === 'string' && currentNode.className.includes('chats-btn')) {
          isEnd = false
        } else if (currentNode.tagName === 'BODY') {
          isEnd = false
          this.SET_INPUT_FOCUSED_ACTION(false)
        }
        currentNode = currentNode.parentNode
      }
    }
  },
  created () {
    this.debouncedSearch = debouncer(value => this.FETCH_SEARCH_BAR_INFO(value), 400)
  },
  mounted () {
    // window.addEventListener('resize', this.handleResize)
    if (window.location.href.includes('catalog/search')) {
      const searchParams = new URLSearchParams(window.location.search)
      if (this.dispatchAction) {
        const queryText = searchParams.get('text')
        this.SET_SEARCH_BAR_VALUE_ACTION(queryText)
      }
    }
  },
  destroyed () {
    window.removeEventListener('resize', this.handleResize)
  },
  watch: {
    isPopupShown (val) {
      if (val && this.isMobile) {
        let scrollTop = window.pageYOffset || document.documentElement.scrollTop
        let scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
        window.onscroll = function () {
          window.scrollTo(scrollLeft, scrollTop)
        }
      } else {
        window.onscroll = function () { }
      }
    },
    isInputFocused (value) {
      if (this.isMobile) {
        this.handleBodyScroll()
        if (!value) this.$refs.searchInput.blur()
      }
    },
    searchBarValue () {
      if (this.isMobile) {
        this.handleBodyScroll()
      }
    },
    shouldHistoryShow () {
      if (this.isMobile) {
        this.handleBodyScroll()
      }
    }
  }
}
</script>
